import React from 'react';
import { Trans } from '@lingui/react/macro';
import { Button, Card } from '@ygtechnologies/rubus-ui';
import { MarkdownDisplay, Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';

import SelectedCorner from '@/buyer/components/SelectedCorner/SelectedCorner';
import { classnames } from '@/utils/html';
import { ConfiguratorPresetDto } from '@/services/configurator';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';

import theme from './PresetList.module.scss';

interface ItemProps {
    style: ConfiguratorPresetDto;
    readOnly: boolean;
    onDetailsClick: (style: ConfiguratorPresetDto) => void;
    onSelectClick: (style: ConfiguratorPresetDto) => void;
}

const PresetListItem: React.FC<ItemProps> = ({ style, readOnly, onDetailsClick, onSelectClick }) => {
    const { getOptionPresetImageThumbnailUrl } = useImageUrls();
    const renderButton = () => {
        if (style.isPicked) {
            return (
                <Button variant="primary" onClick={() => onDetailsClick(style)}>
                    <Trans>Selected</Trans>
                </Button>
            );
        }
        if (readOnly) {
            return (
                <Button variant="tertiary" onClick={() => onDetailsClick(style)}>
                    <Trans>Details</Trans>
                </Button>
            );
        }
        return (
            <Button variant="tertiary" onClick={() => onSelectClick(style)} disabled={readOnly}>
                <Trans>View style</Trans>
            </Button>
        );
    };

    return (
        <Card variant="flat" className={classnames(theme.presetCard, { [theme.selected]: style.isPicked })}>
            <SelectedCorner visible={style.isPicked} />
            <div className={theme.presetImage}>
                {style.images.length > 0 && (
                    <Thumbnail src={getOptionPresetImageThumbnailUrl(style.images[0].id)} />
                )}
            </div>
            <div className={theme.presetContent}>
                <h5 className={theme.presetHeader}>{style.name}</h5>
                <div className={theme.presetDescription}>
                    <MarkdownDisplay markdown={style.description} />
                </div>
                {renderButton()}
            </div>
        </Card>
    );
};

interface Props {
    styles: ConfiguratorPresetDto[];
    readOnly: boolean;
    onSelectClick: (style: ConfiguratorPresetDto) => void;
    onDetailsClick: (style: ConfiguratorPresetDto) => void;
}

export const PresetList: React.FC<Props> = ({ styles, readOnly, onSelectClick, onDetailsClick }) => {
    return (
        <div className={theme.presetGrid}>
            {styles.map((style) => (
                <PresetListItem
                    key={style.id}
                    style={style}
                    onDetailsClick={onDetailsClick}
                    onSelectClick={onSelectClick}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
};
